/**
 * Real-time Lip Sync Integration Example
 * This demonstrates how to use the new real-time audio-driven lip sync system
 */

import { LipSyncController } from "../modules/LipSyncController.minimal.js";
import { RealtimeLLMToElevenLabs } from "../modules/RealtimeLLMToElevenLabs.js";

// Example usage with minimal LipSyncController
export class RealTimeLipSyncExample {
    constructor(characterMesh, elevenLabsApiKey, voiceId) {
        this.characterMesh = characterMesh;
        this.elevenLabsApiKey = elevenLabsApiKey;
        this.voiceId = voiceId;
        
        this.setupLipSync();
    }

    setupLipSync() {
        // 1) Create minimal lip sync controller bound to your mesh
        this.lips = new LipSyncController({ 
            model: this.characterMesh 
        });
        
        // Alternative: Use with blendShapeControls
        // this.lips = new LipSyncController({ 
        //     blendShapeControls: yourBlendShapeControlsMap 
        // });

        console.log('[RealTimeLipSync] Minimal LipSync controller created');
        console.log('[RealTimeLipSync] Supported phonemes:', this.lips.getSupportedPhonemes());
    }

    async startConversation() {
        // 2) Create and configure TTS with lip sync
        this.tts = new RealtimeLLMToElevenLabs({
            voiceId: this.voiceId,
            elevenLabsApiKey: this.elevenLabsApiKey,
            outputFormat: "pcm_16000"
        });

        // 3) Enable real-time lip sync
        this.tts.enableLipSync(this.lips, {
            frameMs: 33,        // Update every 33ms (~30fps)
            minRms: 0.015,      // Silence threshold
            hissRatio: 1.35,    // Fricative detection
            fftSize: 1024,      // FFT size for frequency analysis
        });

        console.log('[RealTimeLipSync] Real-time lip sync enabled');

        // 4) Start the TTS session
        await this.tts.start();
        
        return this.tts;
    }

    // Feed LLM tokens to the system
    handleLLMToken(delta) {
        if (this.tts) {
            this.tts.handleLLMDelta(delta);
        }
    }

    // End the conversation
    endConversation() {
        if (this.tts) {
            this.tts.flushAndClose();
            this.tts = null;
        }
    }

    // Manual phoneme testing
    testPhoneme(phoneme, intensity = 0.7) {
        this.lips.applyPhoneme(phoneme, intensity);
    }

    // Reset lips to neutral
    resetLips() {
        this.lips.reset();
    }
}

// Usage example:
/*
// Initialize with your character mesh
const lipSyncExample = new RealTimeLipSyncExample(
    yourCharacterMesh, 
    'your_elevenlabs_api_key',
    'your_voice_id'
);

// Start a conversation
const tts = await lipSyncExample.startConversation();

// Feed tokens from OpenAI streaming response
openaiStream.on('token', (delta) => {
    lipSyncExample.handleLLMToken(delta);
});

// End conversation when done
lipSyncExample.endConversation();

// Manual testing
lipSyncExample.testPhoneme('AAA', 0.8);  // Test vowel sound
lipSyncExample.testPhoneme('MBP', 0.6);  // Test consonant
lipSyncExample.resetLips();              // Return to neutral
*/